import React, { useEffect, useState } from 'react';
import apiService from '../services/api';
import PageBackground from '../components/PageBackground';
import './BhaktiBalChitraKatha.css';

const ALL_CATEGORIES = 'all';

const BhaktiBalChitraKatha = () => {
  const imageUrl = '/images/bg.avif';
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingChannel, setLoadingChannel] = useState(false);
  const [error, setError] = useState('');

  const loadChannelDetails = async (channelId) => {
    setLoadingChannel(true);
    setError('');

    try {
      const response = await apiService.getBalChitraKathaChannelById(channelId);
      const channelData = response?.data || null;
      setSelectedChannel(channelData);
      setSelectedVideo(channelData?.videos?.[0] || null);
    } catch (err) {
      setError(err.message || 'Unable to load channel videos.');
    } finally {
      setLoadingChannel(false);
    }
  };

  const loadChannels = async (categoryId = ALL_CATEGORIES) => {
    setLoading(true);
    setError('');

    try {
      const channelResponse = await apiService.getBalChitraKathaChannels(
        categoryId === ALL_CATEGORIES ? '' : categoryId
      );
      const channelList = channelResponse?.data || [];

      setChannels(channelList);

      if (channelList.length > 0) {
        await loadChannelDetails(channelList[0].id);
      } else {
        setSelectedChannel(null);
        setSelectedVideo(null);
      }
    } catch (err) {
      setError(err.message || 'Unable to load channels.');
      setChannels([]);
      setSelectedChannel(null);
      setSelectedVideo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError('');

      try {
        const categoryResponse = await apiService.getBalChitraKathaCategories();
        setCategories(categoryResponse?.data || []);
        await loadChannels(ALL_CATEGORIES);
      } catch (err) {
        setError(err.message || 'Unable to load Bhakti Bal Chitra Katha.');
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    document.body.classList.add('bhakti-bal-chitra-katha-theme');
    return () => {
      document.body.classList.remove('bhakti-bal-chitra-katha-theme');
    };
  }, []);

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    await loadChannels(categoryId);
  };

  return (
    <>
      <PageBackground imageUrl={imageUrl} />
      <div className="bal-katha-page">
      <div className="bal-katha-header">
        <h1>Bhakti Bal Chitra Katha</h1>
        <p>Simple, child-friendly spiritual animation channels curated for free viewing.</p>
      </div>

      <div className="bal-katha-category-nav">
        <button
          type="button"
          className={selectedCategory === ALL_CATEGORIES ? 'active' : ''}
          onClick={() => handleCategoryChange(ALL_CATEGORIES)}
        >
          All
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            type="button"
            className={selectedCategory === category.id ? 'active' : ''}
            onClick={() => handleCategoryChange(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      {error && <p className="bal-katha-error">{error}</p>}

      <div className="bal-katha-layout">
        <aside className="bal-katha-channels">
          <h2>Channels</h2>
          {loading ? (
            <p>Loading channels...</p>
          ) : channels.length === 0 ? (
            <p>No channels available for this category.</p>
          ) : (
            <ul>
              {channels.map((channel) => (
                <li key={channel.id}>
                  <button
                    type="button"
                    className={selectedChannel?.id === channel.id ? 'active' : ''}
                    onClick={() => loadChannelDetails(channel.id)}
                  >
                    <img src={channel.thumbnail} alt={channel.name} />
                    <div>
                      <strong>{channel.name}</strong>
                      <span>{channel.videoCount} videos</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="bal-katha-player">
          {loadingChannel ? (
            <p>Loading videos...</p>
          ) : selectedVideo ? (
            <>
              <div className="bal-katha-video-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.youtubeVideoId}?rel=0&modestbranding=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="bal-katha-video-meta">
                <h3>{selectedVideo.title}</h3>
                <p>{selectedChannel?.description}</p>
                <div className="bal-katha-meta-row">
                  <span>{selectedVideo.duration}</span>
                  <span>Age: {selectedVideo.ageGroup}</span>
                  <a
                    href={`https://www.youtube.com/watch?v=${selectedVideo.youtubeVideoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch on YouTube
                  </a>
                </div>
              </div>
              <div className="bal-katha-video-list">
                <h4>More from {selectedChannel?.name}</h4>
                <ul>
                  {(selectedChannel?.videos || []).map((video) => (
                    <li key={video.id}>
                      <button
                        type="button"
                        className={selectedVideo?.id === video.id ? 'active' : ''}
                        onClick={() => setSelectedVideo(video)}
                      >
                        {video.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bal-katha-credit-note">
                <p><strong>Courtesy:</strong> {selectedChannel?.name}</p>
                <p>
                  <strong>Original Channel:</strong>{' '}
                  <a
                    href={selectedChannel?.youtubeChannelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedChannel?.youtubeChannelUrl}
                  </a>
                </p>
                <p>© Content belongs to respective creator. Anandmaya only provides discovery and access through official YouTube embeds.</p>
              </div>
            </>
          ) : selectedChannel ? (
            <div className="bal-katha-empty-channel">
              <h3>{selectedChannel.name}</h3>
              <p>{selectedChannel.description}</p>
              <p>No curated videos added yet for this channel.</p>
              <a
                href={selectedChannel.youtubeChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Channel on YouTube
              </a>
            </div>
          ) : (
            <p>Select a channel to start watching.</p>
          )}
        </section>
      </div>
      </div>
    </>
  );
};

export default BhaktiBalChitraKatha;
